import React, { Component } from 'react';

import { flushSync } from 'react-dom';

import queryString from 'query-string';

import { serverErrorMessage } from './serverErrors';

import './css/main.css';

import { Configure } from './components/configure/Configure';
import { Details } from './components/details/Details';
import { Implement } from './components/implement/Implement';
import { Header } from './components/header/Header';
import { Footer } from './components/footer/Footer';
import { Message } from './components/info/Message';
import { PlanPrompt } from './components/configure/PlanPrompt';
import { matomoTrackSection, matomoTrackCustomEvent } from './MatomoTracking';
import { addProperty as addBasketProperty, removeProperty as removeBasketProperty } from './basket';
import { computeSelectedVendors } from './selectedVendors';

// Sharing moved out of the flow in issue #71 Phase 1, so the wizard runs
// Configure, Details, Implement and ends at Implement. The intro is a
// collapsible splash panel on the Configure step, not a separate step.
// Shown when generating a Resource Key fails with nothing a customer can
// act on: a network failure, a malformed response, or a refusal whose body
// carried no reason. A refusal that does carry one shows that instead, as
// only the service knows which of the supplied keys is at fault (issue
// #121 defect 1).
export const DEFAULT_RESOURCE_ERROR =
    'Could not generate a Resource Key from the supplied License Key. ' +
    'Please check the key and try again.';

const MAX_SECTION = 3;
const IMPLEMENT_SECTION = 3;
const DETAILS_SECTION = 2;
const MIN_SECTION = 1;

import { findVendor } from './vendor';
import { buildFreePropertiesFromMaps } from './freeProperties';
export { findVendor };


// The cloud's accessibleproperties endpoint keys vendors by the engine's
// ElementDataKey (e.g. "FODid"), but the configurator's metadata reports the
// vendorId lowercased (e.g. "fodid") — see MetaDataMapper and
// 51Degrees/common-metadata#304. The free/paid lookups below index
// availableProperties.Products by the metadata VendorId and are case-sensitive,
// so a property like "fodid.IdProbGlobal" would never match the response's
// "FODid" key and would be wrongly treated as paid/inaccessible. Realign the
// response keys to lowercase to match the metadata namespace.
function normalizeVendorKeys(accessibleProperties) {
    if (!accessibleProperties || !accessibleProperties.Products) {
        return accessibleProperties;
    }
    var products = {};
    Object.keys(accessibleProperties.Products).forEach((vendorId) => {
        products[vendorId.toLowerCase()] = accessibleProperties.Products[vendorId];
    });
    return Object.assign({}, accessibleProperties, { Products: products });
}

const newLicenseTypes = {
    FREE: 'free',
    NONE: 'none'
};

class App extends Component {
    displayName = App.name

    constructor(props) {
        super(props);
        this.next = this.next.bind(this);
        this.previous = this.previous.bind(this);
        this.goto = this.goto.bind(this);
        this.updateTerms = this.updateTerms.bind(this);
        this.addToBasket = this.addToBasket.bind(this);
        this.removeFromBasket = this.removeFromBasket.bind(this);
        this.readParams = this.readParams.bind(this);
        this.getProperties = this.getProperties.bind(this);
        this.register = this.register.bind(this);
        this.onSelectVendors = this.onSelectVendors.bind(this);

        // Create empty state to start with. Some elements might not be 
        // initialised from calls to external services.
        this.state = {
            section: MIN_SECTION,
            sectionUpdated: -1,
            plans: [],
            freeIntent: false,
            // The paid property awaiting confirmation after being added
            // to the basket, or null when no prompt is showing. Each
            // property only ever prompts once per visit, tracked by id.
            planPrompt: null,
            promptedPaidIds: [],
            // When true the Configure step lists only free properties.
            filterFreeOnly: false,
            // The intro splash on the Configure step starts expanded and
            // covers the property area until the user collapses it.
            splashCollapsed: false,
            resourceError: '',
            selectedProperties: [],
            paidProperties: [],
            termsAgreed: false,
            receiveMarketingMessages: false,
            receiveProductUpdates: false,
            vendors: [],
            selectedVendors: [],
            components: [],
            categories: [],
            properties: [],
            examples: [],
            productDetails: [],
            resource: {},
            availableProperties: {},
            productKeys: [],
            clientEmail: '',
            clientDomains: [],
            clientDomainsString: '',
            selectAll: false,
            loading: true,
            error: '',
            referer: '',
            newLicenseType: newLicenseTypes.FREE,
            permittedLicenseTypes: [newLicenseTypes.FREE, newLicenseTypes.NONE],
            registered: false,
            showRegisterSuccess: false,
            showRegisterFailed: false,
            // The server's reason when it refuses a registration, for example
            // a selection the License Key's plan does not cover (issue #75).
            registerFailedMessage: null,
            showRegisterEmailFailed: false,
            loadingLicense: 0,
        };

        // Common message to use if data load fails.
        const messLoadFail = 'Failed to load, try refreshing your browser ' +
            'or contact support@51degrees.com.';

        // Start by getting all the data needed to populate the initial
        // state. The resource key is no longer fetched here because
        // generating one requires a valid License Key (issue #71). It
        // is fetched by fetchNewResource once keys are validated.
        //
        // Each endpoint is fetched independently and a failure yields a
        // fallback rather than rejecting, because only the property
        // catalogue is essential. Failing the whole load on any one
        // request meant a single blocked or slow endpoint, whether from a
        // privacy extension, an ad blocker or a transient error, left the
        // customer with nothing but the generic message and no way
        // forward. The plan labels, examples and product details all
        // degrade to sensible empties.
        const load = (ep, fallback) => fetch(ep)
            .then(r => {
                if (!r.ok) {
                    throw new Error(ep + ' returned HTTP ' + r.status);
                }
                return r.json();
            })
            .catch(ex => {
                // Detail goes to the console for support to read.
                console.error('Could not load ' + ep + '.', ex);
                return fallback;
            });

        Promise.all([
            load('api/data/components', []),
            load('api/data/categories', []),
            load('api/data/properties', null),
            load('api/data/vendors', []),
            load('api/data/plans', []),
            load('api/data/examples', {}),
            load('api/data/productinfo', []),
            load('api/data/freeproducts', [])
        ])
            .then(([componentData,
                categoryData,
                propertyData,
                vendorData,
                plansData,
                exampleData,
                productData,
                freeProductsData]) => {

                // Without the property catalogue there is nothing to
                // configure, so that alone is worth the error message.
                if (propertyData == null) {
                    this.setState({ error: messLoadFail });
                    return;
                }

                var selectedVendorsData = computeSelectedVendors(
                    vendorData, componentData, propertyData);

                // Build the initial free/paid map from metadata Maps field.
                // Properties whose Maps include a free product are free.
                var availablePropertiesData = buildFreePropertiesFromMaps(
                    propertyData, freeProductsData);

                // Set the state with the returned data. Use flush sync to
                // ensure the state is up to date before resolving the promise.
                flushSync(() => this.setState({
                    components: componentData,
                    categories: categoryData,
                    properties: propertyData,
                    vendors: vendorData,
                    plans: plansData,
                    availableProperties: availablePropertiesData,
                    examples: exampleData,
                    productDetails: productData,
                    loading: false,
                    selectedVendors: selectedVendorsData
                }));

                this.readParams();
                this.getConfig();
            }).catch((ex) => {
                // The detail goes to the console for support to read;
                // users only ever see the friendly message.
                console.error('Initial data load failed.', ex);
                this.setState({ error: messLoadFail });
            });
    }

    componentDidMount() {
        window.onbeforeunload = function (e) {
            e.preventDefault();
            e.returnValue = '';
        };
    }

    // Passed a configurator ID which is then fetched from global storage. If
    // a response is available then add the properties returned as a space or
    // comma separated list to the basket. Used to pre-configure a result from
    // the configurator.
    getProperties = (id) => {
        fetch('/props/' + id)
            .then(response => response.text())
            .then(data => data.split(/[, +]/).forEach(property => {
                this.addToBasket(property, true);
            }));
    }

    // Checks the location path for a configurator ID and populate associated 
    // properties if present and valid.
    getConfig = () => {
        // Strip the leading slash so the id is passed without it; otherwise
        // getProperties builds "/props//<id>" (double slash), which misses the
        // route and returns index.html instead of the property list. Hosting
        // behind a proxy that collapses "//" hides this, but Kestrel does not.
        let id = window.location.pathname.replace(/^\/+/, '');

        if (id.length > 0) {
            this.getProperties(id);
            // A pre-configured link wants the properties, not the intro.
            this.setState({ splashCollapsed: true });
        }
    }

    // Every property included in the free products, by id. The free set
    // comes from accessibleproperties without a key, so pre-selecting it
    // never drags in a billable related property.
    freePropertyIds = () => {
        var available = this.state.availableProperties;
        var ids = [];
        if (available && available.Products) {
            Object.keys(available.Products).forEach((vendorId) => {
                available.Products[vendorId].Properties.forEach((prop) => {
                    var id = (vendorId + "." + prop.Name).toUpperCase();
                    var match = this.state.properties.find(
                        (p) => p.Id.toUpperCase() === id);
                    if (match) {
                        ids.push(match.Id);
                    }
                });
            });
        }
        return ids;
    }

    // A route chosen on the intro splash. Collapse the splash to reveal
    // the properties. freeIntent pre-selects the free properties so the
    // basket starts as a ready-made free configuration, then goes
    // straight to the sign-up form on the License Key step.
    startFromSplash = (freeIntent) => {
        var update = { splashCollapsed: true, freeIntent: freeIntent };
        if (freeIntent) {
            var joined = this.state.selectedProperties.slice();
            this.freePropertyIds().forEach((id) => {
                if (joined.indexOf(id) === -1) {
                    joined.push(id);
                }
            });
            update.selectedProperties = joined;
            update.section = DETAILS_SECTION;
        }
        this.setState(update);
    }

    // Expand or collapse the intro splash on the Configure step.
    toggleSplash = () => {
        this.setState({ splashCollapsed: !this.state.splashCollapsed });
    }

    // Generate a Resource Key once at least one License Key has been
    // validated. The backend refuses generation without one (issue #71).
    fetchNewResource = () => {
        // Cleared before the guard below rather than after it, so a message
        // from an earlier attempt cannot outlive the condition that caused
        // it. Defensive: no call site currently reaches the guard, because
        // addKey is the only caller and only ever runs with a key just
        // added, and nothing in the UI removes a key.
        this.setState({ resourceError: '' });

        // Send the keys '+'-joined and unencoded, the same convention as
        // updateAccessibleProperties and the GetSubscriptionIDs calls, so
        // the server sees space-separated keys after query decoding rather
        // than a literal '+'.
        var keys = this.state.productKeys.map(pk => pk.value).join('+');
        if (keys === '') {
            return Promise.resolve();
        }
        // The chain is returned so a caller, and a test, can wait for it.
        return fetch('api/data/newresource?license=' + keys)
            .then(async (response) => {
                if (response.ok === false) {
                    // Marked as the service's own words. The catch below
                    // shows only these; every other rejection reaching it
                    // is internal and must not be put in front of a
                    // customer.
                    var refusal = new Error(await serverErrorMessage(
                        response, DEFAULT_RESOURCE_ERROR));
                    refusal.isServerRefusal = true;
                    throw refusal;
                }
                return response.json();
            })
            .then((data) => this.setState({ resource: data }, this.setCanContinue))
            .catch((error) => {
                // Surface the failure and keep the Implement step blocked
                // (setCanContinue requires resource.key).
                //
                // A network failure or a malformed body rejects this chain
                // too, and 'Failed to fetch' or 'Unexpected token < in JSON
                // at position 0' says nothing to a customer while replacing
                // wording that does. Only a refusal carries a message meant
                // for them.
                this.setState({
                    resourceError: error.isServerRefusal === true
                        ? error.message
                        : DEFAULT_RESOURCE_ERROR
                }, this.setCanContinue);
            });
    }

    // Configures the state based on query string parameters.
    // Possible query string keys are:
    // selectedProperties : a comma separated list of properties as either the
    //   common name or the id.See this.state.properties for possible values.
    // implement : a configuration key used to return pre-set properties and
    //   then display the third step showing how to use the properties.
    // ref : a referer identifier used to monitor sharing of configuration.
    // vendors: the vendors to show in the UI to start with.
    readParams = () => {
        let params = queryString.parse(this.props.location.search);

        // Deep links want the properties, so collapse the intro splash.
        if (params.selectedProperties || params.selectall ||
            params.vendors) {
            this.setState({ splashCollapsed: true });
        }

        if (params.selectedProperties) {

            var props = params.selectedProperties.split(/[,+]/g);

            let tmp = props.map((s) => {
                let prop = undefined;
                prop = this.state.properties.find((property) =>
                    property.Name.toUpperCase() === s.toUpperCase() ||
                    property.Id.toUpperCase() == s.toUpperCase());
                if (prop) {
                    return prop.Id;
                }
                return undefined;
            });

            tmp.forEach((prop) => {
                this.addToBasket(prop, true);
            });
        }

        if (params.implement) {
            var resourceKey = params.implement;

            var res = {
                key: resourceKey,
                url: "https://cloud.51degrees.com/api/v4/" + resourceKey + ".js"
            };

            this.setState({
                section: IMPLEMENT_SECTION,
                resource: res,
                implementRecap: true
            });

            this.getProperties(resourceKey);
        }

        if (params.ref) {
            this.setState({
                referer: params.ref
            });
        }

        if (params.selectall && params.selectall === "true") {
            var tmpProps = this.state.properties.map((property) => {
                return property.Id;
            });

            tmpProps.forEach((prop) => {
                this.addToBasket(prop, true);
            });
        }

        if (params.vendors) {
            var vendors = params.vendors.split(/[\s,\+]+/);

            var vendorMaps = [].concat.apply([], this.state.vendors.map((vendor) => {
                return vendor.map;
            }));

            var selectedVendors = [];

            vendorMaps.forEach(vm => {
                if (vendors.indexOf(vm) !== -1) {
                    selectedVendors.push(vm);
                };
            });

            if (selectedVendors.length > 0) {
                this.setState({
                    selectedVendors: selectedVendors
                });
            }
        }
    }

    next = (e) => {
        e.preventDefault();
        // Leaving Configure with paid properties in the selection asks
        // for one confirmation, naming the plan the selection needs.
        // Acknowledged properties never prompt again this visit.
        if (this.state.section === MIN_SECTION) {
            var unacknowledged = this.state.paidProperties.filter(
                (p) => this.state.promptedPaidIds.indexOf(p.Id) === -1);
            if (unacknowledged.length > 0) {
                this.setState({ planPrompt: this.state.paidProperties });
                return;
            }
        }
        // Registration is what records the selection against the Resource
        // Key, and the server refuses one the License Key does not entitle
        // (issue #75). Stay on Details when it is refused: the Resource Key
        // exists either way, because the SPA mints a bare one as soon as a
        // License Key validates, so advancing would present a key carrying
        // none of the selection. The refusal names the properties to remove,
        // and Details is the only step that renders it.
        if (this.state.section === DETAILS_SECTION) {
            this.register().then((registered) => {
                if (registered) {
                    this.advanceSection();
                }
            });
            return;
        }
        this.advanceSection();
    }

    advanceSection = () => {
        var _section = this.state.section < MAX_SECTION ?
            this.state.section + 1 : MAX_SECTION;
        this.setState({ section: _section });
    }

    previous = (e) => {
        e.preventDefault();
        var _section = this.state.section > MIN_SECTION ?
            this.state.section - 1 : MIN_SECTION;
        this.setState({ section: _section });
    }

    goto = (_section) => {
        this.setState({ section: _section });
    }

    onSelectVendors = (item) => {
        if (this.state.selectedVendors.indexOf(item) === -1) {
            var joined = this.state.selectedVendors.concat(item);
            this.setState({ selectedVendors: joined });
        } else {
            var temp = this.state.selectedVendors;
            var index = temp.indexOf(item);
            if (index > -1) {
                temp.splice(index, 1);
            }
            this.setState({ selectedVendors: temp });
        }
    }

    updateTerms = () => {
        this.setState(
            { termsAgreed: !this.state.termsAgreed },
            () => this.setCanContinue());
        matomoTrackCustomEvent("TermsAgreed: ", !this.state.termsAgreed);
    }

    // True when the property id is inside the accessible (free or
    // already-licensed) set. Case-insensitive to match freePropertyIds.
    isFreePropertyId = (id) => {
        var prop = this.state.properties.find((s) => s.Id === id);
        if (prop === undefined) {
            return false;
        }
        var products = this.state.availableProperties.Products;
        return products !== undefined &&
            products.hasOwnProperty(prop.VendorId) &&
            products[prop.VendorId].Properties.some((p) =>
                prop.Id.toUpperCase() ===
                (prop.VendorId + "." + p.Name).toUpperCase());
    }

    // Adds the property provided to the array of selected properties including
    // any related properties on which the provided property relies.
    addToBasket = (property) => {

        var previouslySelected = this.state.selectedProperties;

        // Create a new array of selected properties and replace the existing
        // array.
        var joined = addBasketProperty(
            property, previouslySelected, this.state.properties);

        // A free property must never quietly pull a paid dependency into
        // the selection. Keep only the property itself, anything already
        // selected, and free companions; a paid dependency has to be an
        // explicit choice.
        if (this.isFreePropertyId(property)) {
            joined = joined.filter((id) =>
                id === property ||
                previouslySelected.indexOf(id) !== -1 ||
                this.isFreePropertyId(id));
        }

        flushSync(() => this.setState({ selectedProperties: joined }));

        let availableProperties = this.state.availableProperties;
        let selectedProperties = []
        joined.forEach((property) => {
            var prop = this.state.properties.find((s) => s.Id === property);
            if (prop !== undefined) {
                selectedProperties.push(prop);
            }
        });

        let paidProperties = [];
        selectedProperties.forEach((property) => {
            var free = false;
            if (availableProperties.Products.hasOwnProperty(property.VendorId)) {
                // Case-insensitive, matching freePropertyIds, so the free
                // classifier and the splash route can never disagree over
                // casing between the catalogue and the property list.
                free = availableProperties.Products[property.VendorId].Properties.some((prop) => {
                    return property.Id.toUpperCase() ===
                        (property.VendorId + "." + prop.Name).toUpperCase();
                });
            }
            if (!free)
                return paidProperties.push(property);
        });
        flushSync(() => this.setState({ paidProperties: paidProperties }));

        if (paidProperties.length > 0) {
            // Remove the free account as an option and default to none.
            this.addPermittedLicenseType(newLicenseTypes.NONE);
            this.removePermittedLicenseType(newLicenseTypes.FREE);
            this.setNewLicenseType(newLicenseTypes.NONE);
        } else if (this.state.newLicenseType !== newLicenseTypes.NONE) {
            this.addPermittedLicenseType(newLicenseTypes.FREE);
            this.setNewLicenseType(newLicenseTypes.FREE);
        }

        // Adding paid properties never interrupts selection. The paid
        // plan reminder shows once, when the user progresses to the
        // License Key step (see next).
    }

    // Recompute the paid properties and permitted license types for a
    // selection, mirroring what addToBasket and removeFromBasket do.
    applySelection = (selectedIds) => {
        flushSync(() => this.setState({ selectedProperties: selectedIds }));

        var paidProperties = [];
        selectedIds.forEach((id) => {
            var prop = this.state.properties.find((s) => s.Id === id);
            if (prop !== undefined && this.isFreePropertyId(id) === false) {
                paidProperties.push(prop);
            }
        });
        this.setState({ paidProperties: paidProperties });

        if (paidProperties.length > 0) {
            this.addPermittedLicenseType(newLicenseTypes.NONE);
            this.removePermittedLicenseType(newLicenseTypes.FREE);
            this.setNewLicenseType(newLicenseTypes.NONE);
        } else if (this.state.newLicenseType !== newLicenseTypes.NONE) {
            this.addPermittedLicenseType(newLicenseTypes.FREE);
            this.setNewLicenseType(newLicenseTypes.FREE);
        }
    }

    // Show only the free properties on the Configure step.
    toggleFreeOnly = () => {
        this.setState({ filterFreeOnly: !this.state.filterFreeOnly });
    }

    // Empty the basket after confirmation, so a fresh start does not
    // need fifty individual removals.
    clearSelection = () => {
        var count = this.state.selectedProperties.length;
        if (count === 0) {
            return;
        }
        if (window.confirm('Remove all ' + count +
            ' selected properties?')) {
            this.applySelection([]);
        }
    }

    // Continue past the paid-plan reminder to the License Key step.
    // The acknowledged properties are remembered so re-crossing does
    // not nag unless new paid properties have been added since.
    continuePlanPrompt = () => {
        this.setState({
            planPrompt: null,
            promptedPaidIds: this.state.promptedPaidIds.concat(
                this.state.paidProperties.map((p) => p.Id))
        });
        this.advanceSection();
    }

    // Close the paid-plan reminder and stay on Configure so the
    // selection can be reviewed.
    backPlanPrompt = () => {
        this.setState({ planPrompt: null });
    }

    removeFromBasket = (item) => {
        var tempSelectedProperties = removeBasketProperty(
            item, this.state.selectedProperties, this.state.properties);

        this.setState({ selectedProperties: tempSelectedProperties });

        let availableProperties = this.state.availableProperties;
        let selectedProperties = []
        tempSelectedProperties.forEach((property) => {
            var prop = this.state.properties.find((s) => s.Id === property);
            if (prop !== undefined) {
                selectedProperties.push(prop);
            }
        });

        let paidProperties = [];
        selectedProperties.forEach((property) => {
            var free = false;
            if (availableProperties.Products.hasOwnProperty(property.VendorId)) {
                // Case-insensitive, matching freePropertyIds, so the free
                // classifier and the splash route can never disagree over
                // casing between the catalogue and the property list.
                free = availableProperties.Products[property.VendorId].Properties.some((prop) => {
                    return property.Id.toUpperCase() ===
                        (property.VendorId + "." + prop.Name).toUpperCase();
                });
            }
            if (!free)
                return paidProperties.push(property);
        });
        this.setState({ paidProperties: paidProperties });

        if (paidProperties.length > 0) {
            // Remove the free account as an option and default to none.
            this.addPermittedLicenseType(newLicenseTypes.NONE);
            this.removePermittedLicenseType(newLicenseTypes.FREE);
            this.setNewLicenseType(newLicenseTypes.NONE);
        } else if (this.state.newLicenseType !== newLicenseTypes.NONE) {
            this.addPermittedLicenseType(newLicenseTypes.FREE);
            this.setNewLicenseType(newLicenseTypes.FREE);
        }
    }

    updateAccessibleProperties = () => {
        var tmpProductKeys = this.state.productKeys;
        var params = [];

        if (this.state.registered) {
            params.push('resource=' + this.state.resource.key);
        }
        params.push('license=' + tmpProductKeys.map(pk => pk.value).join('+'));

        fetch('api/data/accessibleproperties?' + params.join('&'))
            .then(result => result.json())
            .then((data) => {
                var available = this.state.availableProperties;
                var nowavailable = Object.assign(available, normalizeVendorKeys(data));

                this.setState({
                    availableProperties: nowavailable
                });
            });
    }

    // Indicate that the data model is being loaded.
    setLoading = (loading) => {
        this.setState({ loadingLicense: loading });
    }

    // Adds the licence key to the map of available licence keys.
    addKey = (key) => {
        if (this.state.productKeys.map(k => k.value).includes(key) === false) {
            var keys = this.state.productKeys.map(k => k.value).join('+');
            Promise.all([
                fetch('api/data/productinfo?licenseKeys=' + key),
                fetch('api/data/GetSubscriptionIDs?licenseKeys=' + keys),
                fetch('api/data/GetSubscriptionIDs?licenseKeys=' + key)
            ])
                .then(([res1, res2, res3]) => Promise.all([
                    res1.json(),
                    res2.json(),
                    res3.json()]))
                .then(([data, ids, keyId]) => {
                    var oldDetails = this.state.productDetails;
                    var newDetails = Object.assign(oldDetails, data);

                    var tmpProductKeys = this.state.productKeys;
                    for (var i = 0; i < data.length; i++) {
                        if (data[i].valid === false) {
                            alert('This key is not valid: ' + data[i].message);
                            this.setLoading(this.state.loadingLicense - 1);
                            return;
                        }

                        var tmpTerms = '';
                        if (data[i].terms) {
                            tmpTerms = 'https://51degrees.com' + data[i].terms;
                        }

                        if (data[i].addon) {
                            if (ids.includes(keyId[0]) === false) {
                                alert('This add-on requires a valid subscription to the 51Degrees Cloud Service, please add a 51Degrees Subscription key first.');
                                this.setLoading(this.state.loadingLicense - 1);
                                return;
                            }
                        }

                        var keyData = {
                            value: key,
                            product: data[i].name,
                            requests: data[i].noOfRequestsPerMonth,
                            addon: data[i].addon,
                            terms: tmpTerms
                        };
                        tmpProductKeys.push(keyData);
                    }

                    this.setState({
                        productDetails: newDetails,
                        productKeys: tmpProductKeys,
                        termsAgreed: true
                    });
                    // data can be empty, in which case no key was added
                    // and there is nothing to track.
                    if (data.length > 0) {
                        matomoTrackCustomEvent("Set Licence Key: '" + key + "'");
                    }

                    // TODO: Only set this if the licenses added so far
                    // actually allow access to the requested properties
                    this.setNewLicenseType(newLicenseTypes.NONE);
                    this.addPermittedLicenseType(newLicenseTypes.NONE);
                    this.removePermittedLicenseType(newLicenseTypes.FREE);
                    // Update accessible properties
                    this.updateAccessibleProperties();
                    // A valid key now exists, so a Resource Key can be
                    // generated (issue #71 gate).
                    this.fetchNewResource();
                });
        } else {
            alert('Key has already been added!');
        }
    }

    validateDetails = () => {
        var domains = this.state.clientDomains;
        let vendors = this.state.vendors;
        var paidProperties = this.state.paidProperties;
        var availableProperties = this.state.availableProperties;
        var email = this.state.clientEmail;
        var productKeys = this.state.productKeys;
        var newLicenseType = this.state.newLicenseType;

        // Validate domain names
        var validIpAddressRegex = new RegExp(/^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/);
        var validHostnameRegex = new RegExp(/^(([a-zA-Z0-9\*]|[a-zA-Z0-9\*][a-zA-Z0-9\-\*]*[a-zA-Z0-9\*])\.)*([A-Za-z0-9\*]|[A-Za-z0-9\*][A-Za-z0-9\-\*]*[A-Za-z0-9\*])$/);

        var invalidDomains = [];
        for (var i = 0; i < domains.length; i++) {
            if ((validIpAddressRegex.test(domains[i]) ||
                validHostnameRegex.test(domains[i])) === false) {
                invalidDomains.push(domains[i]);
            }
        }

        if (invalidDomains.length > 0) {
            alert('\'' + invalidDomains.join(', ') + '\' domain(s) not valid');
            return false;
        }

        // Validate product keys
        if (productKeys.length > 0 &&
            productKeys.filter(p => p.addon).length === productKeys.length) {
            alert('Only key(s) for Add-On(s) have been supplied. ' +
                'To use Add - Ons, a valid subscription to 51Degrees is ' +
                'required.');
            return false;
        }

        var requiredVendors = [];
        paidProperties.forEach((property) => {
            var vendor = findVendor(vendors, property.VendorId);
            // Case-insensitive, matching freePropertyIds: the cloud does not
            // always report names in the catalogue's casing (agent-signature
            // reports them lowercase).
            var propertyName = property.Id.split('.').slice(-1)[0].toUpperCase();
            var available = availableProperties.Products[property.VendorId] &&
                availableProperties.Products[property.VendorId].Properties.find(p =>
                    p.Name.toUpperCase() === propertyName);
            if (requiredVendors.includes(vendor.name) === false &&
                available === undefined) {
                requiredVendors.push(vendor.name);
            }
        });

        if (newLicenseType !== newLicenseTypes.FREE && requiredVendors.length > 0) {
            return window.confirm('Keys for \'' + requiredVendors.join(', ') + '\' are still required, you won\'t be able to access some properties. Are you sure you want to continue?');
        }

        // Validate email
        var validEmailRegex = new RegExp("^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$");
        if ((validEmailRegex.test(String(email)) || email === '') === false) {
            alert('\'' + email + '\' is not a valid email.');
            return false;
        }

        return true;
    }

    // Update the allow domains in the state.
    updateDomains = (domains) => {
        this.setState({
            clientDomainsString: domains,
            clientDomains: domains.length === 0 ? [] :
                domains.replace(/ /g, '').split(',')
        });
    }

    // Set the email in the state.
    setEmail = (email) => {
        this.setState({ clientEmail: email });
    }

    // Register for a resource key for use with the cloud. Resolves true
    // when the selection was recorded against the key and false when it was
    // not, so the caller can keep the customer on Details rather than hand
    // them a key whose entitlements did not persist. Every failure resolves
    // false, not only a refusal: a registration that did not complete leaves
    // the key carrying none of the selection whatever the cause.
    register = () => {
        // Nothing to register until a Resource Key exists, which
        // requires a validated License Key first (issue #71 gate).
        if (!this.state.resource.key) {
            return Promise.resolve(false);
        }
        const submittedEmail = this.state.clientEmail;
        return fetch('/api/configuration/register', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                resourceKey: this.state.resource.key,
                email: submittedEmail,
                domains: this.state.clientDomains.join(' '),
                productKeys: this.state.productKeys.map(k => k.value).join(' '),
                properties: this.state.selectedProperties.join(' '),
                agreedToTerms: this.state.termsAgreed,
                receiveMarketingEmails: this.state.receiveMarketingMessages,
                receiveProductUpdates: this.state.receiveProductUpdates,
                registrationEmailSent: this.state.registered
            })
        }).then((response) => {
            if (!response.ok) {
                this.setState({
                    showRegisterFailed: true,
                    showRegisterSuccess: false,
                    showRegisterEmailFailed: false
                });
                // The server refuses a selection the License Key's plan does
                // not cover, and a selection containing unknown properties
                // (issues #75 and #76). Those refusals carry a message
                // naming what to remove or upgrade, which is the whole
                // point of them, so show it rather than the generic
                // failure. A 429 now joins those refusals for the same
                // reason, because the hourly limit on new Resource Keys
                // (issue #71) is charged here, where the entitlement row
                // is written, rather than at the mint, and its message
                // names how long to wait rather than what to remove.
                if (response.status === 403 || response.status === 400 ||
                    response.status === 429) {
                    // Read by the same helper as fetchNewResource, rather
                    // than by stripping the JSON quotes here: both callers
                    // receive the same body shape from the same framework,
                    // so reading it twice over invited them to diverge.
                    // null leaves the generic failure panel to speak for
                    // itself when the body carries no reason, instead of
                    // blaming the License Key for a registration that
                    // failed for another cause.
                    return serverErrorMessage(response, null)
                        .then(message => {
                            this.setState({
                                registerFailedMessage: message
                            });
                            throw new Error('Registration refused');
                        });
                }
                this.setState({ registerFailedMessage: null });
                alert("Registration failed. Please try again later.");
                throw new Error('Network response was not ok');
            }
            this.setState({
                showRegisterFailed: false,
                registerFailedMessage: null
            });
            return response.json();
        }).then(emailSent => {
            this.setState({
                registered: true,
                showRegisterSuccess: true,
                showRegisterEmailFailed: emailSent === false && submittedEmail && submittedEmail.length > 0
            });
            return true;
        }).catch(error => {
            if (this.state.showRegisterFailed === false) {
                this.setState({ showRegisterFailed: true });
            }
            return false;
        });
    }

    // Update the state with to indicate marketing emails are allowed.
    consentToMarketing = (value) => {
        this.setState({ receiveMarketingMessages: value });
        matomoTrackCustomEvent("receiveMarketingMesseges: ",
            value);
    }

    // Update the state with a flag to indicate product updates are allowed.
    consentToProductUpdates = (value) => {
        this.setState({ receiveProductUpdates: value });
        matomoTrackCustomEvent("receiveProductUpdates: ",
            value);
    }

    // Update the state with the licence type indicator. See newLicenseTypes.
    setNewLicenseType = (licenseType) => {
        this.setState(
            { newLicenseType: licenseType },
            () => this.setCanContinue());
    }

    // Add the license type to the permitted types. See newLicenseTypes.
    addPermittedLicenseType = (licenseType) => {
        let types = this.state.permittedLicenseTypes;
        if (types.indexOf(licenseType) < 0) {
            types.push(licenseType);
            this.setState({ permittedLicenseTypes: types });
        }
    }

    // Remove the permitted license type. See newLicenseTypes.
    removePermittedLicenseType = (licenseType) => {
        let types = this.state.permittedLicenseTypes;
        if (types.indexOf(licenseType) >= 0) {
            types.splice(types.indexOf(licenseType), 1);
            this.setState({ permittedLicenseTypes: types });
        }
    }

    // Enable the user to move to the next step. Every customer, Free
    // and Paid, must hold a valid License Key before a Resource Key
    // can be generated (issue #71). The Resource Key is fetched
    // asynchronously once a key is added, so require it here too, rather
    // than only a key being present. This stops the Implement step
    // rendering before the key exists.
    setCanContinue = () => {
        let canContinue =
            this.state.termsAgreed === true &&
            this.state.productKeys.length > 0 &&
            !!this.state.resource.key;
        this.setState({ canContinue: canContinue });
    }

    // Displays the current section if loading is complete.
    displaySection() {
        let vendors = this.state.vendors;
        let selectedVendors = this.state.selectedVendors;
        // The free-only filter narrows the Configure step (list and
        // search) to properties in the free set.
        let properties = this.state.filterFreeOnly ?
            this.state.properties.filter((p) => this.isFreePropertyId(p.Id)) :
            this.state.properties;
        let categories = this.state.categories;
        let components = this.state.components;
        let examples = this.state.examples;

        if (this.state.loading) {
            if (this.state.error !== '') {
                return (
                    <Message message={this.state.error} />
                );
            } else {
                return (
                    <Message message={'Loading...'} />
                );
            }
        }
        else {
            switch (this.state.section) {
                case 1:
                    matomoTrackSection(
                        this.state.section,
                        this.state.sectionUpdated,
                        "Configure",
                        this.setState.bind(this));
                    return (
                        <Configure
                            selectedProperties={this.state.selectedProperties}
                            availableProperties={this.state.availableProperties}
                            vendors={vendors}
                            selectedVendors={selectedVendors}
                            onSelectVendors={this.onSelectVendors}
                            properties={properties}
                            categories={categories}
                            components={components}
                            examples={examples}
                            addToBasket={this.addToBasket}
                            removeFromBasket={this.removeFromBasket}
                            splashCollapsed={this.state.splashCollapsed}
                            onToggleSplash={this.toggleSplash}
                            onStartFromSplash={this.startFromSplash}
                            onClearSelection={this.clearSelection}
                            freeOnly={this.state.filterFreeOnly}
                            onToggleFreeOnly={this.toggleFreeOnly}
                        />);
                case 2:
                    matomoTrackSection(
                        this.state.section,
                        this.state.sectionUpdated,
                        "Details",
                        this.setState.bind(this));
                    return (
                        <Details
                            selectedProperties={this.state.selectedProperties}
                            paidProperties={this.state.paidProperties}
                            availableProperties={this.state.availableProperties}
                            vendors={vendors}
                            properties={properties}
                            components={components}
                            removeFromBasket={this.removeFromBasket}
                            domains={this.state.clientDomainsString}
                            updateDomains={this.updateDomains}
                            addKey={this.addKey}
                            clientEmail={this.state.clientEmail}
                            receiveMarketingMessages={this.state.receiveMarketingMessages}
                            receiveProductUpdates={this.state.receiveProductUpdates}
                            consentToMarketing={this.consentToMarketing}
                            consentToProductUpdates={this.consentToProductUpdates}
                            setEmail={this.setEmail}
                            registered={this.state.registered}
                            productDetails={this.state.productDetails}
                            productKeys={this.state.productKeys}
                            permittedLicenseTypes={this.state.permittedLicenseTypes}
                            newLicenseType={this.state.newLicenseType}
                            removePermittedLicenseType={this.removePermittedLicenseType}
                            addPermittedLicenseType={this.addPermittedLicenseType}
                            setNewLicenseType={this.setNewLicenseType}
                            newLicenseTypes={newLicenseTypes}
                            showRegisterSuccess={this.state.showRegisterSuccess}
                            showRegisterFailed={this.state.showRegisterFailed}
                            registerFailedMessage={this.state.registerFailedMessage}
                            showRegisterEmailFailed={this.state.showRegisterEmailFailed}
                            loadingLicense={this.state.loadingLicense}
                            setLoading={this.setLoading}
                            plans={this.state.plans}
                            freeIntent={this.state.freeIntent}
                            resourceError={this.state.resourceError}
                        />);
                case 3:
                    matomoTrackSection(
                        this.state.section,
                        this.state.sectionUpdated,
                        "Implement",
                        this.setState.bind(this));
                    return (
                        <Implement
                            selectedProperties={this.state.selectedProperties}
                            properties={properties}
                            examples={examples}
                            resource={this.state.resource}
                            referer={this.state.referer}
                        />);
                default:
                    matomoTrackSection(
                        this.state.section,
                        this.state.sectionUpdated,
                        "Error",
                        this.setState.bind(this));
                    return (
                        <main className="g-main">
                            <div className="g-panel g-panel--100">
                                <article className="g-info">
                                    <div>Error...</div>
                                </article>
                            </div>
                        </main>
                    );
            }
        }
    }

    render() {
        var classes = "l-index";

        switch (this.state.section) {
            case 1:
                classes += " p-configure";
                break;
            case 2:
                classes += " p-details";
                break;
            case 3:
                classes += " p-implement";
                break;
            default:
                break;
        }

        return (
            <div className={classes}>
                <div className="l-index__content">
                    <Header
                        termsAgreed={this.state.termsAgreed}
                        hasResource={!!this.state.resource.key}
                        section={this.state.section}
                        goto={this.goto}
                    />
                    {this.displaySection()}
                    <PlanPrompt
                        properties={this.state.planPrompt}
                        plans={this.state.plans}
                        selectedProperties={this.state.selectedProperties
                            .map((id) => this.state.properties.find(
                                (p) => p.Id === id))
                            .filter((p) => p !== undefined)}
                        onContinue={this.continuePlanPrompt}
                        onBack={this.backPlanPrompt}
                    />
                    <Footer
                        removeSelectedProperty={this.removeFromBasket}
                        properties={this.state.properties}
                        selectedProperties={this.state.selectedProperties}
                        availableProperties={this.state.availableProperties}
                        vendors={this.state.vendors}
                        section={this.state.section}
                        termsAgreed={this.state.termsAgreed}
                        updateTerms={this.updateTerms}
                        next={this.next}
                        previous={this.previous}
                        canContinue={this.state.canContinue}
                        permittedLicenseTypes={this.state.permittedLicenseTypes}
                        newLicenseTypes={newLicenseTypes}
                        validateDetails={this.validateDetails}
                        matomoTrackSection={this.matomoTrackSection}
                    />
                </div>
            </div>
        );
    }
}

// Work around for deprecation of withRouter - https://stackoverflow.com/questions/71837081/export-withrouter-imported-as-withrouter-was-not-found-in-react-router-do

import { useLocation, useNavigate, useParams } from 'react-router-dom';

const withRouter = Component => props => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    return (
        <Component
            {...props}
            location={location}
            navigate={navigate}
            params={params}
        />
    );
};

// The unwrapped component, for tests that need the instance that owns
// fetchNewResource and register. The default export below is the wrapped
// one the application renders.
export { App };

const AppWithRouter = withRouter(App);

function AppWithMatomo() {
    React.useEffect(() => {
        var _mtm = window._mtm = window._mtm || [];
        _mtm.push({ 'mtm.startTime': (new Date().getTime()), 'event': 'mtm.Start' });
        var d = document, g = d.createElement('script'), s = d.getElementsByTagName('script')[0];
        g.async = true; g.src = 'https://cdn.matomo.cloud/51degrees.matomo.cloud/container_irj7x47W.js'; s.parentNode.insertBefore(g, s);
    }, [])

    return <AppWithRouter />;
}

export default AppWithMatomo;